import { useCallback, useEffect, useRef, useState } from "react";

export type AutosaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

// Saves edits shortly after they're made (and on demand, e.g. when an input
// loses focus or the page is left) instead of behind a Save button.
//
// `save` should persist everything outstanding and reject if it didn't; it is
// called one at a time, and anything changed while a save is in flight is
// saved straight after, once the page has re-rendered with that save's result.
// A failed save isn't retried on its own (offline that would be a request
// loop): the next edit, or "Retry", tries again.
const useAutosave = ({
  save,
  enabled,
  delay = 1200,
  saveOnLeave,
}: {
  save: () => Promise<void>;
  enabled: boolean;
  delay?: number;
  // Whether leaving the page saves what's outstanding (default yes). A new
  // track left before its first save is discarded, as the leave prompt says.
  saveOnLeave?: () => boolean;
}) => {
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const hasPendingRef = useRef(false);
  const isSavingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Bumped when edits came in during a save: the follow-up save runs from an
  // effect, so it's built from the state that save left (a new track's id).
  const [followUpCount, setFollowUpCount] = useState(0);
  // Keep the latest save (it closes over form state) without re-arming timers.
  const saveRef = useRef(save);
  saveRef.current = save;
  const saveOnLeaveRef = useRef(saveOnLeave);
  saveOnLeaveRef.current = saveOnLeave;

  // The save in flight, so leaving the page can wait for it rather than run
  // alongside it.
  const inFlightRef = useRef<Promise<void> | null>(null);
  // Callers waiting on a save in flight, and on whatever is saved after it.
  const waitersRef = useRef<((saved: boolean) => void)[]>([]);

  // Resolves true once everything outstanding is saved, false if a save failed.
  const run = useCallback(async (): Promise<boolean> => {
    if (isSavingRef.current) {
      // Anything outstanding is saved straight after the save in flight.
      return new Promise<boolean>((resolve) =>
        waitersRef.current.push(resolve),
      );
    }
    if (!hasPendingRef.current) {
      return true;
    }
    hasPendingRef.current = false;
    isSavingRef.current = true;
    setStatus("saving");
    const saving = saveRef.current();
    inFlightRef.current = saving;
    let saved = true;
    try {
      await saving;
    } catch {
      saved = false;
    }
    isSavingRef.current = false;
    inFlightRef.current = null;
    if (!saved) {
      hasPendingRef.current = true;
      setStatus("error");
    } else if (hasPendingRef.current) {
      setStatus("pending");
      setFollowUpCount((count) => count + 1);
      return new Promise<boolean>((resolve) =>
        waitersRef.current.push(resolve),
      );
    } else {
      setStatus("saved");
    }
    const waiters = waitersRef.current;
    waitersRef.current = [];
    waiters.forEach((resolve) => resolve(saved));
    return saved;
  }, []);

  useEffect(() => {
    if (followUpCount > 0) {
      run();
    }
  }, [followUpCount, run]);

  // Called on every edit: pushes the save out until the edits stop.
  const scheduleSave = useCallback(() => {
    if (!enabled) {
      return;
    }
    hasPendingRef.current = true;
    // A save in flight still says so: edits made meanwhile are saved after it.
    setStatus((current) => (current === "saving" ? current : "pending"));
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(run, delay);
  }, [delay, enabled, run]);

  // Save now (input blurred, "Retry"). Waits for a save in flight, and
  // resolves false if anything outstanding couldn't be saved.
  const flushSave = useCallback(() => {
    clearTimeout(timeoutRef.current);
    return run();
  }, [run]);

  // Leaving the page (or switching tracks): save what's outstanding. A save in
  // flight is waited for, not run alongside (a new track would be created
  // twice), and the follow-up after it is started here: once the page has
  // gone there is no render to start it.
  const saveBeforeLeaving = useCallback(() => {
    clearTimeout(timeoutRef.current);
    if (saveOnLeaveRef.current && !saveOnLeaveRef.current()) {
      hasPendingRef.current = false;
      return;
    }
    if (inFlightRef.current) {
      inFlightRef.current.catch(() => null).then(run);
    } else {
      run();
    }
  }, [run]);

  useEffect(() => saveBeforeLeaving, [saveBeforeLeaving]);

  useEffect(() => {
    window.addEventListener("pagehide", saveBeforeLeaving);
    return () => window.removeEventListener("pagehide", saveBeforeLeaving);
  }, [saveBeforeLeaving]);

  // Closing or reloading the tab cancels a request it starts, so the save
  // above can't be relied on there. While something isn't saved yet, start
  // the save and have the browser ask first: staying lets it finish.
  useEffect(() => {
    const askIfUnsaved = (event: BeforeUnloadEvent) => {
      if (!hasPendingRef.current && !isSavingRef.current) {
        return;
      }
      // Not dropped here: staying on the page must keep the edits.
      if (!saveOnLeaveRef.current || saveOnLeaveRef.current()) {
        saveBeforeLeaving();
      }
      event.preventDefault();
      // Older browsers only ask when this is set.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", askIfUnsaved);
    return () => window.removeEventListener("beforeunload", askIfUnsaved);
  }, [saveBeforeLeaving]);

  // Drop what's waiting to be saved (discarding a new track), so leaving the
  // page doesn't save it. A save already in flight still finishes.
  const cancelSave = useCallback(() => {
    clearTimeout(timeoutRef.current);
    hasPendingRef.current = false;
  }, []);

  return { status, scheduleSave, flushSave, cancelSave };
};

export default useAutosave;
